import type { BrandSpecials } from '@iheartjane/data-access';
import type { ProductCardProps } from '@iheartjane/menu';
import { hideStrikethroughPrice } from '@iheartjane/menu';
import compact from 'lodash-es/compact';

import { getGoldLabel } from '@jane/bloom/utils/badges/getGoldLabel';
import { hasSpecialApplied as hasSpecial } from '@jane/bloom/viewModels/getPrice';
import type { ProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearch';

export function getDefaultWeight(
  product: ProductSearchResultViewModel
): string {
  const formattedWeightKeys = Object.keys(product.availableWeightsFormatted);
  return formattedWeightKeys[0] ?? product.available_weights?.[0] ?? 'each';
}

export function getDefaultWeightPricing(product: ProductSearchResultViewModel) {
  const defaultWeightPricing =
    product.availableWeightsFormatted[getDefaultWeight(product)];
  const price = defaultWeightPricing?.priceFormatted;
  const priceBeforeDiscounts = defaultWeightPricing?.fullPriceFormatted;
  return {
    price,
    priceBeforeDiscounts,
    priceIsDiscounted: Boolean(
      priceBeforeDiscounts && price !== priceBeforeDiscounts
    ),
  };
}

export interface BuildProductCardPropsArgs {
  atMaxQuantity: boolean;
  brandSpecials: BrandSpecials;
  cartCount: number;
  isAddingToCart: boolean;
  isPlacement?: boolean;
  isSponsored?: boolean;
  preloaded?: boolean;
  product: ProductSearchResultViewModel;
  productReviewsEnabled: boolean | undefined;
  storeState: string | undefined;
}

export function buildProductCardProps({
  atMaxQuantity,
  brandSpecials,
  cartCount,
  isAddingToCart,
  isPlacement,
  isSponsored,
  preloaded,
  product,
  productReviewsEnabled,
  storeState,
}: BuildProductCardPropsArgs) {
  const { price: defaultWeightPrice, priceIsDiscounted } =
    getDefaultWeightPricing(product);
  const hasSpecialApplied = Boolean(hasSpecial(product));
  const isDiscounted = hasSpecialApplied && priceIsDiscounted;

  const janeGoldLabel = product.applicable_brand_special_ids
    ? getGoldLabel({
        applicableBrandSpecialIds: product.applicable_brand_special_ids,
        brandSpecials,
        lowestAvailablePriceVal:
          product.lowest_available_price &&
          'val' in product.lowest_available_price
            ? product.lowest_available_price.val
            : undefined,
      })
    : null;

  return {
    atMaxQuantity,
    brandInfo: compact([product.brand, product.brand_subtype]).join(' '),
    cartCount,
    dosageInfo: product.dosageAmountPackInfo,
    eagerLoad: Boolean(preloaded),
    goldTagLabel:
      product.shouldShowGold && janeGoldLabel ? janeGoldLabel : null,
    hasMultipleWeightDiscount: isDiscounted && product.hasMultipleWeights,
    hasMultipleWeights: product.hasMultipleWeights,
    hideStrikethrough: hideStrikethroughPrice(storeState ?? ''),
    imageAlt: product.name,
    imageSizes: product.imageSrcSet?.sizes,
    imageSrcSet: product.imageSrcSet?.srcSet,
    imageUrl: product.image_urls?.[0],
    isAddingToCart,
    isDiscounted,
    isPlacement,
    isSponsored,
    name: product.name,
    originalPrice: product.priceLabel,
    potencyInfo: product.basePotencyLabResults ?? product.potencyLabResults,
    price: defaultWeightPrice ? product.discountedPriceLabel : undefined,
    ratingSummary: productReviewsEnabled
      ? product.aggregateRatingAndCount
      : null,
    specialLabel: product.specialLabel,
  } satisfies Partial<ProductCardProps>;
}
